import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Globe from './Globe';
import { httpsify } from '../utils/url';
import { tripMonthlyStorage, fmtUSD } from '../utils/cost';
import { mapLinks } from '../utils/geo';
import { isTrashed } from '../data/firestore';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};

function TripTile({ trip, thumbs }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const preview = httpsify(trip.highlightPreviewUrl);
  const poster = httpsify(trip.highlightPosterUrl);
  const hasLoc = Number.isFinite(trip.lat) && Number.isFinite(trip.lng);
  const links = mapLinks(trip.lat, trip.lng);

  const onEnter = () => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  };
  const onLeave = () => {
    const v = videoRef.current;
    if (v) v.pause();
  };

  return (
    <div
      className="cs-tripcard"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={() => navigate(`/trips/${trip.id}`)}
    >
      {/* Main video / highlight section */}
      <div className="cs-tripcard-main">
        {poster ? (
          <div className="cs-tripcard-poster" style={{ backgroundImage: `url(${poster})` }} />
        ) : (
          <div className="cs-tripcard-mainfallback">🎬</div>
        )}
        {preview && (
          <video
            ref={videoRef}
            className="cs-tripcard-video"
            src={preview}
            muted
            loop
            playsInline
            preload="none"
          />
        )}
        <div className="cs-tripcard-grad" />
        <div className="cs-tripcard-title">{trip.name}</div>
      </div>

      <div className="cs-tripcard-body">
        {/* Map + meta row */}
        <div className="cs-tripcard-row">
          <div className="cs-tripcard-map">
            <Globe lat={trip.lat} lng={trip.lng} size={104} zoom={3.2} dot={hasLoc} />
          </div>
          <div className="cs-tripcard-side">
            <div className="meta">
              {[trip.location, trip.startDate].filter(Boolean).join(' · ') || 'No location set'}
            </div>
            <div className="meta" style={{ marginTop: 6 }}>
              <span className="cs-badge">{trip.assetCount || 0} files</span> {fmtBytes(trip.totalBytes)}
              {trip.totalBytes > 0 && (
                <span title="Estimated monthly storage"> · ~{fmtUSD(tripMonthlyStorage(trip))}/mo</span>
              )}
            </div>
            {links && (
              <div className="cs-tripcard-links">
                <a href={links.earth} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                  🌍 Earth
                </a>
                <a href={links.maps} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                  🛰 Satellite
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Thumbnail strip */}
        {thumbs.length > 0 && (
          <div className="cs-tripcard-thumbs">
            {thumbs.map((a) => (
              <div key={a.id} className="cs-tripcard-thumb" style={{ backgroundImage: `url(${httpsify(a.thumbUrl)})` }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Large-tile grid of trips with a stylized earth globe, hover-play highlight,
// and a thumbnail strip. `assets` (optional, from useAllFootage) supplies
// per-trip thumbnails.
export default function TripsGrid({ title = 'Trips', trips, assets = [], onNewTrip }) {
  // Soft-deleted trips never appear in the grid (homepage or Trips page).
  const visible = (trips || []).filter((t) => !isTrashed(t));
  const thumbsByTrip = (tripId) =>
    assets.filter((a) => a.tripId === tripId && a.thumbUrl && !isTrashed(a)).slice(0, 4);

  return (
    <div className="cs-tripsection">
      {(title || onNewTrip) && (
        <div className="cs-page-head" style={{ padding: 0, marginBottom: 16 }}>
          <h2 style={{ fontSize: 22 }}>{title}</h2>
          {onNewTrip && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onNewTrip}>
              + New trip
            </button>
          )}
        </div>
      )}
      {visible.length === 0 ? (
        <div className="cs-empty">No trips yet. Create one to back up its raw footage.</div>
      ) : (
        <div className="cs-tripgrid">
          {visible.map((t) => (
            <TripTile key={t.id} trip={t} thumbs={thumbsByTrip(t.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
